/**
 * Chat Properties Page
 * 
 * A professional teacher selection and session management interface that provides:
 * - Teacher selection with detailed profiles
 * - Previous sessions filtered by selected teacher
 * - Session continuation and management
 * - Professional UI with smooth animations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import TeacherCard from '../../components/ui/TeacherCard';
import LearningSessionCard from '../../components/ui/LearningSessionCard';
import ApiService from '../../services/apiService';

const ChatProperties = () => {
  const { profileId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [learningSessions, setLearningSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [currentProfileName, setCurrentProfileName] = useState('');
  const [studentPicture, setStudentPicture] = useState(null);

  // Fetch initial data
  const fetchInitialData = useCallback(async (userId) => {
    try {
      setLoading(true);
      
      if (!userId || userId === 'undefined' || userId === 'null') {
        console.error('Invalid userId provided:', userId);
        setError('Invalid user ID. Please try again.');
        setLoading(false);
        return;
      }
      
      // Fetch profile
      const profileData = await ApiService.getProfile(userId);
      if (profileData) {
        setCurrentProfileName(profileData.name || `${profileData.firstName} ${profileData.lastName}`);
        setStudentPicture(profileData.avatar);
      }
      
      // Fetch all teachers
      const teachersData = await ApiService.getAllTeachers();
      setTeachers(teachersData);
      
      // Fetch learning sessions
      const sessionsData = await ApiService.getLearningSessionsByUser(userId);
      setLearningSessions(sessionsData.learningSessions || []);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter sessions by selected teacher
  useEffect(() => {
    if (selectedTeacher) {
      const filtered = learningSessions.filter(session => 
        session.teacherId === selectedTeacher.id
      );
      setFilteredSessions(filtered);
    } else {
      setFilteredSessions([]);
    }
  }, [selectedTeacher, learningSessions]);

  // Initialize data on mount
  useEffect(() => {
    if (!profileId && !currentUser) return;
    
    const userId = profileId || currentUser?.uid;
    if (!userId) {
      console.error('No valid user ID available');
      setLoading(false);
      return;
    }
    
    fetchInitialData(userId);
  }, [profileId, currentUser, fetchInitialData]);

  // Handle teacher selection
  const handleTeacherSelect = useCallback((teacher) => {
    setSelectedTeacher(teacher);
  }, []);

  // Handle start new session
  const handleStartNewSession = useCallback(() => {
    if (!selectedTeacher) {
      setError('Please select a teacher first');
      return;
    }
    
    navigate(`/chat/${profileId || currentUser?.uid}/realtime`, {
      state: {
        teacher: selectedTeacher,
        isNewSession: true
      }
    });
  }, [selectedTeacher, profileId, currentUser, navigate]);

  // Handle continue session
  const handleContinueSession = useCallback((sessionId) => {
    if (!selectedTeacher) {
      setError('Please select a teacher first');
      return;
    }
    
    navigate(`/chat/${profileId || currentUser?.uid}/realtime`, {
      state: {
        teacher: selectedTeacher,
        sessionId: sessionId,
        isResuming: true
      }
    });
  }, [selectedTeacher, profileId, currentUser, navigate]);

  // Handle delete session
  const handleDeleteSession = useCallback(async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this learning session?')) {
      return;
    }

    try {
      await ApiService.deleteLearningSession(sessionId);
      setLearningSessions(prev => prev.filter(s => s.id !== sessionId));
      setFilteredSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4 animate-pulse">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Your Learning Environment</h3>
            <p className="text-gray-600">Setting up your personalized session...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 pt-28 pb-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back to Dashboard</span>
            </button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {currentProfileName?.charAt(0) || 'S'}
                </span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Start Your Learning Session
                </h1>
                <p className="text-gray-600 text-lg">
                  Choose your teacher and begin your personalized learning journey
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 mb-1">Error</h4>
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Teacher Selection Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Select Your Teacher</h2>
            <div className="text-sm text-gray-500">
              {teachers.length} teachers available
            </div>
          </div>
          
          {teachers.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Teachers Available</h3>
              <p className="text-gray-600">Please check back later for available teachers.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teachers.map((teacher) => (
                <div 
                  key={teacher.id} 
                  onClick={() => handleTeacherSelect(teacher)}
                  className={`cursor-pointer transform transition-all duration-300 hover:scale-105 ${
                    selectedTeacher?.id === teacher.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                >
                  <TeacherCard
                    teacher={teacher}
                    isSelected={selectedTeacher?.id === teacher.id}
                    onSelect={() => handleTeacherSelect(teacher)}
                    showActions={false}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Teacher Actions */}
        {selectedTeacher && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  {selectedTeacher.picture ? (
                    <img
                      src={selectedTeacher.picture}
                      alt={selectedTeacher.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {selectedTeacher.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedTeacher.name}</h3>
                  <p className="text-gray-600">{selectedTeacher.specialty} • {selectedTeacher.level}</p>
                </div>
              </div>
              
              <button
                onClick={handleStartNewSession}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 hover:shadow-lg transform hover:scale-105"
              >
                Start New Session
              </button>
            </div>
          </div>
        )}

        {/* Previous Sessions Section */}
        {selectedTeacher && filteredSessions.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Previous Sessions with {selectedTeacher.name}
              </h2>
              <div className="text-sm text-gray-500">
                {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredSessions.map((session) => (
                <LearningSessionCard
                  key={session.id}
                  {...session}
                  onContinue={handleContinueSession}
                  onDelete={handleDeleteSession}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Sessions Message */}
        {selectedTeacher && filteredSessions.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Previous Sessions</h3>
              <p className="text-gray-600 mb-6">
                You haven't had any learning sessions with {selectedTeacher.name} yet.
              </p>
              <button
                onClick={handleStartNewSession}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
              >
                Start Your First Session
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ChatProperties;

